import { useEffect, useState, type ReactNode } from "react";
import { cn } from "../lib/cn";

const LOGO_URL =
  "https://www.insurancerevolution.co.uk/wp-content/uploads/2024/03/header_logo.png";

/**
 * The wordmark is a fixed-colour PNG on the marketing site, so it needs a light
 * plate to sit on in dark mode rather than being inverted — inverting turns the
 * brand red into cyan.
 */
export function Logo({
  className,
  height = 32,
  plate = true,
}: {
  className?: string;
  height?: number;
  plate?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-md",
        plate && "bg-white px-2 py-1 shadow-ir-sm",
        className,
      )}
    >
      <img
        src={LOGO_URL}
        alt="Insurance Revolution"
        style={{ height }}
        className="w-auto object-contain"
        loading="eager"
      />
    </span>
  );
}

/** Compact gold monogram for collapsed sidebars and avatars. */
export function LogoMark({
  size = 34,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size }}
      className={cn(
        "grid shrink-0 place-items-center rounded-xl bg-gold-sheen font-display text-sm font-bold text-ink-950 shadow-ir-gold",
        className,
      )}
    >
      IR
    </span>
  );
}

export function BrandLockup({
  subtitle,
  className,
}: {
  subtitle?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <LogoMark />
      <span className="min-w-0">
        <span className="block truncate font-display text-sm font-semibold tracking-tight text-content">
          Insurance Revolution
        </span>
        {subtitle && (
          <span className="block truncate text-xs text-content-subtle">{subtitle}</span>
        )}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Avatar                                                                      */
/* -------------------------------------------------------------------------- */

export function Avatar({
  name,
  email,
  src,
  size = 36,
  className,
}: {
  name?: string | null;
  email?: string | null;
  src?: string | null;
  size?: number;
  className?: string;
}) {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  const source = (name ?? "").trim() || (email ?? "").split("@")[0] || "";
  const words = source.split(/[\s._-]+/).filter(Boolean);
  const initials =
    words.length === 0
      ? "?"
      : words.length === 1
        ? words[0].slice(0, 2).toUpperCase()
        : (words[0][0] + words[words.length - 1][0]).toUpperCase();

  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name ?? email ?? "User"}
        width={size}
        height={size}
        onError={() => setBroken(true)}
        className={cn("shrink-0 rounded-full object-cover", className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
      className={cn(
        "grid shrink-0 place-items-center rounded-full border border-gold-300/60 bg-gold-100 font-semibold text-gold-800",
        "dark:border-gold-800 dark:bg-gold-950/70 dark:text-gold-300",
        className,
      )}
    >
      {initials}
    </span>
  );
}
